import PageContainer from "@/pages/containers/page-container";
import { Textarea } from "@/components/ui/textarea";
import { CirclePlus, Loader2, SendHorizontal } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { MessageType } from "@/model/csr-chat";
import {
  CSR_ROLE_BOT,
  CSR_ROLE_CSR,
  CSR_ROLE_MEMBER,
} from "@/services/csr-chat/csr-chat-constants";
import {
  useChannelMessages,
  useChatChannels,
  useMembersChatToken,
  useCsrTyping,
} from "@/hooks/use-csr-chat";
import {
  emitNewMessage,
  emitMessageSeenStatus,
} from "@/services/csr-chat/socket-service";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCsrMessages,
  selectCsrTypingEditor,
  selectCsrIsConnected,
  selectCsrChannelId,
  setChannelId,
  addMessage,
  clearUnread,
  setPendingFile,
} from "@/store/csr-chat/csr-chat-slice";
import ChatHeader from "./ChatHeader";
import { MessageBubble } from "./MessageBubble";
import EmptyChatState from "./EmptyChatState";
import IsTypingUI from "./IsTypingUI";
import useUserInfo from "@/hooks/use-user-info";
import { AuthMode } from "@/context/auth-provider";
import { QueryKeys } from "@/constants";
import { queryClient } from "@/hooks/use-query-client";
import { Navigate } from "react-router-dom";
import {
  getBubblePosition,
  isValidFile,
  buildPendingFileMessage,
  isMessageSeen,
} from "@/lib/csr-chat-utils";
import { ChatLiabilityAlert } from "@/components/csr-chat/chat-liability-alert";

const INVALID_FILE_DISMISS_MS = 10_000;

const CsrChat = () => {
  const userInfo = useUserInfo();
  const dispatch = useDispatch();
  const isGuest = userInfo.mode === AuthMode.GUEST;
  const isSocketConnected = useSelector(selectCsrIsConnected);
  const isSendDisabled = isGuest || !isSocketConnected;

  const { isLoading: isTokenLoading, isError: isTokenError } =
    useMembersChatToken();
  const {
    data: channels,
    isLoading: isChannelsLoading,
    isError: isChannelsError,
  } = useChatChannels();

  const channelId = useSelector(selectCsrChannelId);

  const showCsrChat = queryClient.getQueryData([
    QueryKeys.GLOBAL_FLAGS,
    userInfo.username,
  ])?.data?.[0]?.showChat;

  const firstChannelId = channels?.[0]?.channel?._id;
  useEffect(() => {
    if (firstChannelId) dispatch(setChannelId(firstChannelId));
  }, [firstChannelId, dispatch]);

  const {
    isLoading: isMessagesLoading,
    isError: isMessagesError,
    oldCount,
    isLoadingMore,
    loadMore,
  } = useChannelMessages(channelId);

  const isLoading =
    isTokenLoading ||
    isChannelsLoading ||
    (Boolean(channelId) && isMessagesLoading);
  const isError = isTokenError || isChannelsError || isMessagesError;

  const messages = useSelector(selectCsrMessages(channelId));
  const typingEditor = useSelector(selectCsrTypingEditor(channelId));

  const lastSentMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === CSR_ROLE_MEMBER) return messages[i]._id;
    }
    return null;
  }, [messages]);

  const lastReceivedMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== CSR_ROLE_MEMBER && m.type !== MessageType.INFO)
        return m._id;
    }
    return null;
  }, [messages]);

  const lastBotPromptId = useMemo(() => {
    const last = messages[messages.length - 1];
    return last?.role === CSR_ROLE_BOT && last?.type === MessageType.MESSAGE
      ? last._id
      : null;
  }, [messages]);

  const [openSeenId, setOpenSeenId] = useState<string | null>(null);

  const handleToggleSeen = useCallback((messageId: string) => {
    setOpenSeenId((prev) => (prev === messageId ? null : messageId));
  }, []);

  const [inputText, setInputText] = useState("");
  const [invalidFile, setInvalidFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef(0);
  const invalidFileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const { onTypingInput, stopTyping } = useCsrTyping(channelId);

  useEffect(() => {
    if (channelId) dispatch(clearUnread());
  }, [channelId, dispatch]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (prevScrollHeightRef.current > 0 && container) {
      // Restore scroll position after prepending older messages.
      container.scrollTop =
        container.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (target.scrollTop === 0 && oldCount > 0 && !isLoadingMore) {
        prevScrollHeightRef.current = target.scrollHeight;
        loadMore();
      }
    },
    [oldCount, isLoadingMore, loadMore],
  );

  useEffect(() => {
    return () => {
      if (invalidFileTimerRef.current)
        clearTimeout(invalidFileTimerRef.current);
    };
  }, []);

  const handleFileAttachment = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      if (!isValidFile(file)) {
        if (invalidFileTimerRef.current)
          clearTimeout(invalidFileTimerRef.current);
        setInvalidFile(true);
        invalidFileTimerRef.current = setTimeout(
          () => setInvalidFile(false),
          INVALID_FILE_DISMISS_MS,
        );
        return;
      }

      const tempMessageId = crypto.randomUUID();
      dispatch(setPendingFile({ tempId: tempMessageId, file }));
      dispatch(
        addMessage(
          buildPendingFileMessage(
            file,
            tempMessageId,
            channelId,
            userInfo.username,
          ),
        ),
      );
    },
    [channelId, userInfo.username, dispatch],
  );

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text) return;

    stopTyping();
    const tempId = crypto.randomUUID();

    emitNewMessage({
      message: text,
      chatChannel: channelId,
      tempId,
      type: MessageType.MESSAGE,
    });

    dispatch(
      addMessage({
        _id: tempId,
        chatChannel: channelId,
        createdAt: new Date().toISOString(),
        username: userInfo.username,
        role: CSR_ROLE_MEMBER,
        message: text,
        deleted: false,
      }),
    );
    setInputText("");
  }, [inputText, channelId, userInfo.username, stopTyping, dispatch]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setInputText(value);
      onTypingInput(value);
    },
    [onTypingInput],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  if (!showCsrChat) return <Navigate to="/" replace />;

  if (isLoading) {
    return (
      <PageContainer headerLeftContent={<ChatHeader />} title="Chats">
        <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="animate-spin" size={32} />
          <p className="text-sm">Getting chat details...</p>
        </div>
      </PageContainer>
    );
  }

  if (isError) {
    const errorMessage = isTokenError
      ? "Failed to authenticate with chat service."
      : isChannelsError
        ? "Failed to load chat channels."
        : "Failed to load messages.";

    return (
      <PageContainer headerLeftContent={<ChatHeader />} title="Chats">
        <div className="flex h-full flex-col items-center justify-center gap-2 text-destructive">
          <p className="text-sm font-medium">{errorMessage}</p>
          <p className="text-xs text-muted-foreground">
            Please try again later.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer headerLeftContent={<ChatHeader />} title="Chats">
      <div className="relative flex h-full flex-col justify-between gap-2">
        <ChatLiabilityAlert />
        <div
          ref={messagesContainerRef}
          className="flex h-[calc(100vh-330px)] flex-col overflow-y-auto px-5 py-3"
          onScroll={handleScroll}
        >
          {isLoadingMore && (
            <div className="flex justify-center py-3">
              <Loader2
                className="animate-spin text-muted-foreground"
                size={20}
              />
            </div>
          )}
          {messages.length === 0 ? (
            <EmptyChatState />
          ) : (
            <>
              <div className="flex-1" />
              {messages.map((msg, i) => {
                const isOutgoing = msg.role === CSR_ROLE_MEMBER;
                const isAlwaysVisible = isOutgoing
                  ? msg._id === lastSentMessageId
                  : msg._id === lastReceivedMessageId;
                const showTick = isAlwaysVisible || openSeenId === msg._id;
                return (
                  <MessageBubble
                    key={msg._id}
                    message={msg}
                    position={getBubblePosition(messages, i)}
                    isSeen={isMessageSeen(msg, userInfo.username)}
                    showTick={showTick}
                    showBotReply={msg._id === lastBotPromptId}
                    onToggleSeen={
                      !isAlwaysVisible
                        ? () => handleToggleSeen(msg._id)
                        : undefined
                    }
                    onVisible={
                      msg.role === CSR_ROLE_CSR
                        ? () => emitMessageSeenStatus(msg._id, msg.chatChannel)
                        : undefined
                    }
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="h-7">
          {typingEditor && <IsTypingUI editor={typingEditor} />}
        </div>

        <hr />

        {invalidFile && (
          <div className="mx-3 my-1 overflow-hidden rounded-lg border border-red-200 bg-red-50 shadow-sm">
            <div className="flex items-start gap-2 px-3 py-2">
              <svg
                className="mt-0.5 size-3.5 shrink-0 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-xs leading-tight text-red-700">
                Invalid file. Only images, PDFs, and documents up to 10 MB are
                allowed.
              </span>
            </div>
            <div className="h-1 w-full bg-red-100">
              <div className="h-full animate-progress bg-red-400" />
            </div>
          </div>
        )}

        <div className="flex max-h-[100px] min-h-[60px] pb-2 pl-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            onChange={handleFileAttachment}
          />
          {/* 3.3.2 Labels or Instructions — A: the field had only a placeholder, and that
              placeholder doubles as a status message here, so it never named the control. */}
          <Textarea
            aria-label="Your message"
            placeholder={
              isGuest
                ? "You cannot send messages"
                : !isSocketConnected
                  ? "Connecting..."
                  : "Your Message"
            }
            value={inputText}
            disabled={isSendDisabled}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="row-1 max-h-[200px] resize-none border-0 shadow-none outline-none focus:outline-none focus-visible:ring-transparent active:outline-none"
          />
          {/*
            2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A. Attach and Send were
            <span onClick>: not focusable, no role, no accessible name — a member using a
            keyboard or screen reader could type a message to member support but had no way
            to send it. `disabled` now conveys the unavailable state to assistive tech,
            rather than an undefined handler plus reduced opacity (1.4.1 Use of Color — A).
          */}
          <div className="flex items-center justify-between gap-2 p-2">
            <button
              type="button"
              aria-label="Attach a file"
              disabled={isSendDisabled}
              className={`rounded-full bg-gray-100 p-3 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${isSendDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-gray-200"}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <CirclePlus aria-hidden="true" height="24px" />
            </button>
            <button
              type="button"
              aria-label="Send message"
              disabled={isSendDisabled}
              className={`rounded-full bg-gray-100 p-3 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${isSendDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-gray-200"}`}
              onClick={handleSend}
            >
              <SendHorizontal aria-hidden="true" height="24px" />
            </button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default CsrChat;
